import { Box, Button, Typography } from '@mui/material';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useAuth } from '@/auth/useAuth';
import { wizardStore } from '@/store/wizard.store';
import { toTitleCase } from '@/utils/text.utils';
import logoUrl from '@/assets/logo.png';

/** Altezza fissa dell'header, condivisa con i layout a colonna piena (es. ConfiguratorPage). */
export const HEADER_HEIGHT = 64;

/**
 * Header dell'app. Sulla rotta del configuratore (`/configurations/:id`) riproduce l'header in-wizard
 * legacy: blocco identità appartamento (tipologia serif-corsivo arancio + sottotitolo config a sinistra),
 * wordmark centrale, azioni a destra (RIEPILOGO → vista riepilogo). "Salva e torna alla home" porta a
 * /apartments. FAQ/TUTORIAL sono deferiti (nascosti). Fuori dal configuratore mostra il brand + email/logout.
 */
export const Header = observer(function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, isAuthenticated, logout } = useAuth();

  const configMatch = matchPath('/configurations/:id', location.pathname);
  const inConfigurator = configMatch !== null;
  const configId = configMatch?.params.id;
  const metadata = wizardStore.metadata;

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        minHeight: HEADER_HEIGHT,
        px: { xs: 2, md: 4 },
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      {inConfigurator && metadata ? (
        <>
          {/* Zona sinistra: identità appartamento/configurazione + azione "salva e torna alla home". */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate('/apartments')}
              sx={{ pl: 0, flexShrink: 0, fontSize: '0.75rem', letterSpacing: '0.04em' }}
            >
              ← Salva e torna alla home
            </Button>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontFamily: '"miller", serif',
                  fontStyle: 'italic',
                  color: 'primary.main',
                  fontSize: '1.05rem',
                  lineHeight: 1.2,
                }}
              >
                {toTitleCase(metadata.apartmentName)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {[metadata.apartmentCode, metadata.configurationName].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </Box>

          {/* Logo grafico centrale (marchio Abitare In). */}
          <Box
            component="img"
            src={logoUrl}
            alt="Abitare In"
            sx={{ height: 40, width: 'auto', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}
          />

          {/* Zona destra: RIEPILOGO (attivo → vista riepilogo). FAQ/Tutorial deferiti (nascosti). */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {configId && (
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate(`/configurations/${configId}/resume`)}
                sx={{ fontSize: '0.75rem', letterSpacing: '0.04em', px: 1.5 }}
              >
                Riepilogo
              </Button>
            )}
          </Box>
        </>
      ) : (
        <>
          <Typography
            variant="h4"
            component="span"
            noWrap
            sx={{ fontWeight: 500, letterSpacing: '0.04em', fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Abitare In{' '}
            <Box component="span" sx={{ color: 'secondary.main' }}>
              — Home Configurator
            </Box>
          </Typography>

          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleLogout}
                aria-label="Esci dall'account"
              >
                Esci
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
});
